import {
  type CarrierContactFragment,
  type CarrierContactType,
  useCreateCarrierContactMutation,
  useUpdateCarrierContactMutation,
} from "@qw/graphql";
import { ButtonGroup, FieldInput, Form, FormReset, FormSubmit, Group, useToast } from "@qw/ui";
import { z } from "zod";

const FormSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().optional(),
  title: z.string().optional(),
});

interface Props {
  carrierContact?: CarrierContactFragment;
  type: CarrierContactType;
  appetiteProductId: string;
  onCompleted: () => void;
}

export const CarrierContactForm: React.FC<Props> = ({ carrierContact, type, appetiteProductId, onCompleted }) => {
  const { toast } = useToast();

  const [createCarrierContact] = useCreateCarrierContactMutation();
  const [updateCarrierContact] = useUpdateCarrierContactMutation();

  const defaultValues = {
    firstName: carrierContact?.firstName || "",
    lastName: carrierContact?.lastName || "",
    email: carrierContact?.email || "",
    phone: carrierContact?.phone ?? "",
    title: carrierContact?.title ?? "",
  };

  const handleSubmit = async (values: z.infer<typeof FormSchema>) => {
    if (carrierContact) {
      return await updateCarrierContact({
        variables: { input: { id: carrierContact.id, ...values } },
        onCompleted: () => {
          toast({ title: "Contact updated" });
          onCompleted();
        },
        refetchQueries: ["Product"],
      });
    }

    return await createCarrierContact({
      variables: { input: { appetiteProductId, type, ...values } },
      onCompleted: () => {
        toast({ title: "Contact created" });
        onCompleted();
      },
      refetchQueries: ["Product"],
    });
  };

  return (
    <Form validationSchema={FormSchema} defaultValues={defaultValues} onSubmit={handleSubmit}>
      <Group>
        <FieldInput name="firstName" label="First name" />
        <FieldInput name="lastName" label="Last name" />
        <FieldInput name="email" label="Email" type="email" />
        <FieldInput name="phone" label="Phone" />
        <FieldInput name="title" label="Title" />

        <ButtonGroup>
          <FormReset onClick={onCompleted} />
          <FormSubmit />
        </ButtonGroup>
      </Group>
    </Form>
  );
};
